import { CallVideoStateConsumer } from 'app/containers/call/CallVideoStateContext';
import { BookingStateConsumer } from 'app/containers/rtc/BookingContext/BookingState';
import { GetDeviceManager, IDeviceManagerActions } from 'app/containers/rtc/DeviceManagerContext';
import { BookingState, BookingTypeSettings } from 'app/models/rtc/booking/constants';
import { ParticipantType } from 'app/models/rtc/constants';
import React, { useEffect } from 'react';
import { useIsWebRTCSupported } from 'app/containers/rtc/BookingContext/useRequiredFeatures';

interface IProps {
  deviceManager: Pick<IDeviceManagerActions, 'start' | 'stop'>;
  bookingState: Pick<BookingState, 'bookingType' | 'participantType'>;
  enableDevices: boolean;
}

export const DeviceManagerEnabler = ({
  bookingState: { bookingType, participantType },
  deviceManager: { start, stop },
  enableDevices,
}: IProps) => {
  const isWebRTCSupported = useIsWebRTCSupported();

  const deviceManagerEnabled = calculateDeviceManagerEnabled({
    isWebRTCSupported,
    participantType,
    enableDevices,
  });

  useEffect(() => {
    if (deviceManagerEnabled) {
      const settings = BookingTypeSettings[bookingType];

      start(settings.deviceSetupBehavior);

      return () => {
        stop();
      };
    }
  }, [bookingType, deviceManagerEnabled, start, stop]);

  return null;
};

const DeviceManagerEnablerInjectDependencies: React.FunctionComponent = ({ children }) => (
  <React.Fragment>
    <GetDeviceManager>{(deviceManager) =>
      <BookingStateConsumer>{(bookingState) => bookingState &&
        <CallVideoStateConsumer>{({ enableDevices }) =>
          <DeviceManagerEnabler
            deviceManager={deviceManager}
            bookingState={bookingState}
            enableDevices={enableDevices}
          />
        }</CallVideoStateConsumer>
      }</BookingStateConsumer>
    }</GetDeviceManager>

    {children}
  </React.Fragment>
);

export const calculateDeviceManagerEnabled = (args: {
  participantType?: ParticipantType,
  enableDevices: boolean,
  isWebRTCSupported: boolean,
}) => {
  if (!args.isWebRTCSupported) {
    return false;
  }

  const { participantType, enableDevices } = args;

  // Only full participants should have device manager enabled,
  // participant type can change dynamically during a call. This
  // should toggle local devices
  //
  // TODO: Replace participantType with method to explicitly control deviceManagerEnabled
  const videoParticipation = participantType === ParticipantType.Participant;

  // Only enable the device manager if the user is participating and the call has video enabled.
  const deviceManagerEnabled = videoParticipation && enableDevices;

  return deviceManagerEnabled;
};

export default DeviceManagerEnablerInjectDependencies;
